import * as React from 'react';
import { useIntl } from 'react-intl';
import {
  CmdInput,
  CmdLabel,
  CmdModal,
  CmdModalFooterButton,
  CmdSimpleSelect,
  CmdSimpleSelectOption,
  CmdSwitch,
} from '@commander-services/gui-components';
import { Controller, useForm } from 'react-hook-form';
import { useQueries } from '@tanstack/react-query';
import FormikValidatorService, {
  IValidationApiResponse,
} from '../../../Services/FormikValidatorService';
import LoaderService from '../../../Services/LoaderService';
import usePageWithForm from '../../../hooks/usePageWithForm';
import { ISimpleSelectOption } from '../../Forms/ArrayFields/interfaces';
import PermissionsService, { PERMISSION_EXPENSE_TYPE } from '../../../Services/PermissionsService';
import { IOperatingCostTypeFormValues, OperationsCostTypeData } from './types';
import { create, getById, getCustomers, getValidation, update } from './OperatingCostTypeService';
import { transformErrors, validateFields } from '../../../Services/HookFormService';
import useAnalytics from '../../../hooks/useAnalytics';
import { IOption } from '../../Forms/CmdField';

interface IOperatingCostTypeFormProps {
  closeCallback: () => void;
  refreshTableCallback: () => void;
  selectedId?: number;
  customerId?: number | null | undefined;
}

type TOperatingCostTypeFormKeys = 'customerId' | 'name' | 'active';

export default function OperatingCostTypeForm(props: IOperatingCostTypeFormProps) {
  usePageWithForm();
  const { trackEvent } = useAnalytics();
  const { formatMessage: f } = useIntl();
  const [validation, setValidation] = React.useState<IValidationApiResponse | null>(null);
  const [customers, setCustomers] = React.useState<ISimpleSelectOption[]>([]);

  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<IOperatingCostTypeFormValues>({
    mode: 'onChange',
    defaultValues: {
      customerId: '',
      name: '',
      active: 1,
    },
    resolver: async (data) => {
      const validateErros = validateFields(data, validation);
      const formErrors = transformErrors(validateErros);
      return {
        values: formErrors && Object.keys(formErrors).length ? {} : data, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  const onSubmit = async (values: IOperatingCostTypeFormValues) => {
    LoaderService.showLoader(true);

    if (props.selectedId) {
      const response = await update(props.selectedId, values);
      if (response) {
        trackEvent(['Oprating costs types form', 'submit', 'Update']);
        props.closeCallback();
        props.refreshTableCallback();
      }
    } else {
      const response = await create(values);
      if (response) {
        trackEvent(['Oprating costs types form', 'submit', 'Create']);
        props.closeCallback();
        props.refreshTableCallback();
      }
    }
    LoaderService.showLoader(false);
  };

  const getValidationData = async () => {
    const data = await getValidation();
    if (data) {
      setValidation(data);
    }
  };

  const getCustomersData = async () => {
    const data = await getCustomers();
    if (data) {
      if (!props.selectedId) {
        const filterOptionsByPermission = data.filter((item: IOption) =>
          PermissionsService.hasAccess(PERMISSION_EXPENSE_TYPE, ['create'], Number(item.value))
        );

        setCustomers(filterOptionsByPermission);
        if (filterOptionsByPermission && filterOptionsByPermission.length === 1) {
          setValue('customerId', String(filterOptionsByPermission[0]?.value));
        }
      } else {
        setCustomers(data);
      }
    }
  };

  const getData = async () => {
    if (props.selectedId) {
      const data = await getById(props.selectedId, props.customerId);

      if (data) {
        Object.keys(getValues()).forEach((fieldName) => {
          const key = fieldName as keyof OperationsCostTypeData;

          if (data[key] !== null) {
            if (key === 'active') {
              setValue(key as TOperatingCostTypeFormKeys, data[key] ? 1 : 0);
            } else {
              setValue(key as TOperatingCostTypeFormKeys, data[key] as string | number);
            }
          }
        });
      }
    }
  };

  const results = useQueries({
    queries: [
      {
        queryKey: ['costTypeData'],
        queryFn: () => getData(),
        enabled: !!props.selectedId,
      },
      {
        queryKey: ['costTypeCustomers'],
        queryFn: () => getCustomersData(),
      },
      {
        queryKey: ['costTypeValidation'],
        queryFn: () => getValidationData(),
      },
    ],
  });

  const { isLoading: isLoadingCostTypeData } = results[0];
  const { isLoading: isLoadingCostTypeCustomers } = results[1];
  const { isLoading: isLoadingCostTypeValidation } = results[2];

  const buttons: CmdModalFooterButton[] = [
    {
      id: 'operating-cost-type-close',
      type: 'button',
      className: 'e-button',
      title: f({ id: 'operatingCostTypeForm.close' }),
      closeCallback: () => {
        trackEvent(['Oprating costs types form', 'click', 'Close form']);
        props.closeCallback();
      },
    },
    {
      id: 'operationcost-form-submit',
      type: 'submit',
      className: 'e-button e-button--gray',
      title: f({ id: 'operatingCostTypeForm.save' }),
      submitCallback: handleSubmit(onSubmit),
    },
  ];

  const getCustomerNameById = (customerId: number, options: ISimpleSelectOption[]): string => {
    const customer = options.find(
      (option: ISimpleSelectOption) => Number(option.value) === customerId
    );
    if (customer) {
      return customer.item;
    }
    return '';
  };

  const isLoadingData =
    isLoadingCostTypeData || isLoadingCostTypeCustomers || isLoadingCostTypeValidation;

  return (
    <CmdModal
      id="operating-cost-type-form"
      title={props.selectedId ? f({ id: 'table.detail' }) : f({ id: 'table.add' })}
      footerButtons={buttons}
      loader={isLoadingData}
    >
      {(props.selectedId && props.customerId) || (customers && customers.length === 1) ? (
        <CmdLabel
          title={f({ id: 'operatingCostTypeForm.customer' })}
          value={
            customers && customers.length === 1
              ? customers[0].item
              : getCustomerNameById(Number(props.customerId), customers)
          }
          areLabels={true}
        />
      ) : (
        <Controller
          name="customerId"
          control={control}
          // rules={{
          //   required: undefined,
          // }}
          render={({ field }) => (
            <CmdSimpleSelect
              {...field}
              id="customerId"
              label={f({ id: 'operatingCostTypeForm.customer' })}
              introOption={f({ id: 'operatingCostTypeForm.choose' })}
              required={FormikValidatorService.isFieldRequired(
                validation ? validation.customerId : []
              )}
              options={customers as CmdSimpleSelectOption[]}
              onChangeCallback={(value) => {
                trackEvent(['Oprating costs types form', 'change', 'Customer', Number(value)]);
                setValue('customerId', value);
                trigger('customerId');
              }}
              error={errors.customerId?.message}
              touched={true}
            />
          )}
        />
      )}
      <Controller
        name="name"
        control={control}
        render={({ field }) => (
          <CmdInput
            {...field}
            id="name"
            name="name"
            type="text"
            label={f({ id: 'operatingCostTypeForm.name' })}
            required={FormikValidatorService.isFieldRequired(validation ? validation.name : [])}
            onChangeCallback={(value) => {
              setValue('name', value);
              trigger('name');
            }}
            error={errors.name?.message}
            touched={true}
          />
        )}
      />
      <Controller
        name="active"
        control={control}
        render={({ field }) => (
          <CmdSwitch
            {...field}
            id="active"
            label={f({ id: 'operatingCostTypeForm.active' })}
            required={FormikValidatorService.isFieldRequired(validation ? validation.active : [])}
            setValue={(value) => {
              setValue('active', value);
              trigger('active');
            }}
          />
        )}
      />
    </CmdModal>
  );
}
